import { addDays, format } from 'date-fns';
import { PeriodType } from '../../../../../../shared/types/prompt/PeriodType';
import { MonitorDataResponse } from '../../../../../../shared/models/monitor/MonitorDataResponse';
import {
  ChartMatrixData,
  GeneralData,
  GeneralDataRecords,
  LinksData,
  StatsTable,
} from '../models/StatsModel';
import {
  normalizeKeyword,
  removeHttpHttps,
  SELECTED_BRAND_COLOR,
} from '../../../../../../shared/utils/utils';
import { getFirstSeriesPalette } from '../../../../../../shared/utils/chartTheme';
import { SOV_PIE_CHART_PALETTE } from '../../../../../../shared/utils/sovPalette';
import {
  KeywordsDailyStatsData,
  KeywordsDailyStatsRecord,
} from '../../../../../../shared/models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import { MonitorPeriod } from '../../../../../../shared/types/monitor/MonitorPeriod';
import { PieChartData } from '../../../../../../shared/models/common/PieChartData';
import { MonitorType } from '../../../../../../shared/types/monitor/MonitorType';
import { DropdownItemData } from '../../../../../../shared/components/dropdown-item/DropdownItem';
import { t } from 'i18next';
import { KeywordResponse } from '../../../../../../shared/models/monitor/KeywordResponse';
import { TagsResponse } from '../../../../../../shared/models/tags/TagsResponse';
import { findTagIdInKeyword } from '../../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../../shared/helpers/tags/TagsHelper';

export const prepareGeneralData = (
  keywordsDailyStatsData: KeywordsDailyStatsData | null,
  selectedBrand: string,
  keywordsMap: Map<string, string>,
  commonKeywords: KeywordResponse[] | null,
  tags: TagsResponse[] | null
): GeneralData | null => {
  if (!keywordsDailyStatsData) {
    return null;
  }

  const shareOfVoiceChartData: PieChartData[] = [];
  const result: GeneralDataRecords = {};

  Object.keys(keywordsDailyStatsData).forEach((key) => {
    const records = keywordsDailyStatsData[key];

    const validPositions = records
      .map((record) => record.averagePosition)
      .filter((value): value is number => value !== null);

    const validShareOfVoice = records
      .map((record) => record.averageShareOfVoice)
      .filter((value): value is number => value !== null && value !== 0);

    const averagePosition = parseFloat(
      (validPositions.reduce((sum, pos) => sum + pos, 0) / (validPositions.length || 1)).toFixed(1)
    );
    const averageShareOfVoicePosition =
      validShareOfVoice.reduce((sum, pos) => sum + pos, 0) / validShareOfVoice.length || 0;
    const averageShareOfVoice = averageShareOfVoicePosition || 0;

    const sortedRecords = records
      .filter((record) => record.averagePosition !== null)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    let trend = undefined;
    if (sortedRecords.length >= 2) {
      const firstPosition = sortedRecords[0].averagePosition!;
      const lastPosition = sortedRecords[sortedRecords.length - 1].averagePosition!;
      trend =
        !lastPosition || !firstPosition
          ? undefined
          : parseFloat(((lastPosition - firstPosition) * -1).toFixed(2));
    }

    const roundedAverageShareOfVoice = Math.floor(averageShareOfVoice * 100) / 100;

    result[key] = {
      averagePosition,
      averageShareOfVoice: roundedAverageShareOfVoice,
      trend,
    };

    shareOfVoiceChartData.push({
      value: roundedAverageShareOfVoice,
      name: key,
    });
  });

  const totalShareOfVoice = shareOfVoiceChartData.reduce((sum, item) => sum + item.value, 0);

  const normalizedChartData = shareOfVoiceChartData.map((item) => ({
    ...item,
    value: totalShareOfVoice > 0 ? Math.floor((item.value / totalShareOfVoice) * 100 * 100) / 100 : 0,
  }));

  normalizedChartData.forEach((item) => {
    if (result[item.name]) {
      result[item.name].averageShareOfVoice = item.value;
    }
  });

  return {
    shareOfVoiceChartData: changeToFullName(
      getPieChartWithOthers(normalizedChartData, selectedBrand, keywordsMap, commonKeywords, tags),
      keywordsMap
    ),
    cumulativeData: result,
  };
};

export const getPieChartWithOthers = (
  shareOfVoiceChartData: PieChartData[],
  forcedBrand: string,
  keywordsMap: Map<string, string>,
  commonKeywords: KeywordResponse[] | null,
  tags: TagsResponse[] | null
): PieChartData[] => {
  const MAX_ITEMS = 15;

  const totalSum = shareOfVoiceChartData.reduce((acc, item) => acc + item.value, 0);
  const sortedData = shareOfVoiceChartData.sort((a, b) => b.value - a.value);
  const forcedBrandData = sortedData.find((item) => item.name === forcedBrand);

  let topItems: PieChartData[];
  let itemsForOthers: PieChartData[];

  if (forcedBrandData) {
    const dataWithoutForced = sortedData.filter((item) => item.name !== forcedBrand);
    topItems = [forcedBrandData, ...dataWithoutForced.slice(0, MAX_ITEMS - 1)];
    itemsForOthers = dataWithoutForced.slice(MAX_ITEMS - 1);
  } else {
    topItems = sortedData.slice(0, MAX_ITEMS);
    itemsForOthers = sortedData.slice(MAX_ITEMS);
  }

  topItems = topItems.map((item) => ({
    ...item,
    value: item.value,
  }));

  if (itemsForOthers.length > 0) {
    const othersSum = itemsForOthers.reduce((acc, item) => acc + item.value, 0);
    const othersPercentage = (othersSum / totalSum) * 100;
    const others = {
      name: t('others'),
      value: parseFloat(othersPercentage.toFixed(2)),
    };
    topItems = [...topItems, others];
  }

  topItems.forEach((item) => {
    if (item.name === t('others')) {
      return;
    }

    if (!tags || !commonKeywords) {
      item.name = keywordsMap.get(item.name) ?? item.name;
      return;
    }

    const tagId = findTagIdInKeyword(item.name, commonKeywords);
    const keyword = getTagLabel(tags, tagId, item.name);
    item.name = keywordsMap.get(keyword) ?? keyword;
  });

  const currentSum = topItems.reduce((acc, item) => acc + item.value, 0);

  if (currentSum < 100) {
    const missingPercentage = parseFloat((100 - currentSum).toFixed(2));
    const othersIndex = topItems.findIndex((item) => item.name === t('others'));

    if (othersIndex !== -1) {
      topItems[othersIndex].value = parseFloat(
        (topItems[othersIndex].value + missingPercentage).toFixed(2)
      );
    } else {
      topItems.push({
        name: t('others'),
        value: missingPercentage,
      });
    }
  }

  return topItems;
};

export const changeToFullName = (pieChartData: PieChartData[], keywordsMap: Map<string, string>) => {
  const items = [...pieChartData];
  return items.map((item) => {
    if (item.name === t('others')) {
      return item;
    }

    const fullName = keywordsMap.get(item.name);
    return {
      ...item,
      name: fullName ?? item.name,
    };
  });
};

export const createBrandsMatrix = (
  periodType: PeriodType,
  keywordsDailyStatsData: KeywordsDailyStatsData | null,
  selectedKeyword: string,
  keywordsMap: Map<string, string>,
  commonKeywords: KeywordResponse[],
  tags: TagsResponse[] | null
): ChartMatrixData => {
  if (!keywordsDailyStatsData) {
    return { matrix: [], secondMatrix: [], colorsMap: new Map<string, string>() };
  }

  const colorsMap = new Map<string, string>();
  const allBrands = Object.keys(keywordsDailyStatsData);

  const brandAverages = allBrands.map((brand) => {
    const records = keywordsDailyStatsData[brand];
    const avg = records.reduce((sum, record) => sum + (record.averagePosition ?? 0), 0) / records.length;
    return { brand, avg };
  });

  let topBrands = brandAverages
    .sort((a, b) => a.avg - b.avg)
    .slice(0, 6)
    .map((item) => item.brand);

  if (!topBrands.includes(selectedKeyword) && allBrands.includes(selectedKeyword)) {
    topBrands.pop();
    topBrands.push(selectedKeyword);
  }
  const headers: string[] = ['date'];
  headers.push(...topBrands.map((b) => keywordsMap.get(b) ?? b));

  const uniqueDates = Object.entries(keywordsDailyStatsData).map(([, records]) =>
    records.map((record) => record.date.toString())
  )[0];

  const matrix: (string | number)[][] = [];

  uniqueDates.forEach((date) => {
    const row: (string | number)[] = [prepareHeaderDate(date, periodType)];
    topBrands.forEach((brand, index) => {
      const record = keywordsDailyStatsData[brand].find((rec) => rec.date.toString() === date);

      const value: number | null = record?.averagePosition ?? null;
      row.push(value ?? (null as unknown as string | number));
      const brandDisplayName = keywordsMap.get(brand) ?? brand;
      const isSelectedBrand = normalizeKeyword(brand).toLowerCase() === normalizeKeyword(selectedKeyword).toLowerCase();
      colorsMap.set(brandDisplayName, isSelectedBrand ? SELECTED_BRAND_COLOR : SOV_PIE_CHART_PALETTE[index % SOV_PIE_CHART_PALETTE.length]);
    });
    matrix.push(row);
  });

  matrix.unshift(headers);
  fillMissingData(matrix);

  return { matrix, secondMatrix: [], colorsMap };
};

export const prepareHeaderDate = (date: string, periodType: PeriodType): string => {
  if (periodType === 'WEEKLY') {
    let toDate = addDays(new Date(date), 6);
    const now = new Date();

    if (toDate > now) {
      toDate = now;
    }

    return `${date} - ${format(toDate, 'yyyy-MM-dd')}`;
  }

  if (periodType === 'MONTHLY') {
    return format(date, 'yyyy-MM');
  }

  return date;
};

const fillMissingData = (matrix: (string | number)[][]): void => {
  const numberOfColumns = matrix[0].length;
  const numberOfRows = matrix.length;

  for (let col = 1; col < numberOfColumns; col++) {
    for (let row = numberOfRows - 1; row > 0; row--) {
      const value = matrix[row][col];

      if (value === null || (typeof value === 'number' && value > 30)) {
        matrix[row][col] = 31;
      }
    }
  }
};

export const createKeywordMatrix = (
  periodType: PeriodType,
  keywordsDailyStatsRecords: KeywordsDailyStatsRecord[]
): ChartMatrixData => {
  if (!keywordsDailyStatsRecords) {
    return { matrix: [], secondMatrix: [], colorsMap: new Map<string, string>() };
  }
  const matrixMonitor: (string | number)[][] = [];
  const headersAveragePosition: string[] = ['date', t('averagePosition')];

  const uniqueDates: Set<string> = new Set(
    keywordsDailyStatsRecords.map((record) => record.date.toString())
  );
  const uniqueHeadersAveragePosition = Array.from(new Set(headersAveragePosition));
  const colorsMap = new Map<string, string>([['Average position', getFirstSeriesPalette()[0]]]);

  uniqueDates.forEach((date) => {
    const headerDate = prepareHeaderDate(date, periodType);
    const rowAveragePosition: (string | number)[] = [headerDate];
    const record = keywordsDailyStatsRecords.find((record) => record.date.toString() === date);
    rowAveragePosition.push(record?.averagePosition ?? (null as unknown as string | number));
    matrixMonitor.push(rowAveragePosition);
  });

  matrixMonitor.unshift(uniqueHeadersAveragePosition);
  fillMissingData(matrixMonitor);

  return { matrix: matrixMonitor, secondMatrix: [], colorsMap };
};

export const prepareStatsTableData = (
  prompt: string,
  period: MonitorPeriod,
  monitorData: MonitorDataResponse,
  keywordsDailyStatsRecords: KeywordsDailyStatsRecord[]
): StatsTable => {
  let allRecords: MonitorDataResponse = {
    data: {},
    pagination: monitorData?.pagination,
  };

  if (period === 'DAILY') {
    allRecords = monitorData;
  } else {
    Object.values(monitorData.data)
      .flat()
      .forEach((record) => {
        const date = format(new Date(record.created), 'yyyy-MM-dd');
        if (!allRecords.data[date]) {
          allRecords.data[date] = [];
        }
        allRecords.data[date].push(record);
      });
  }
  const statsTableData = Object.keys(allRecords.data).map((date) => {
    const record = keywordsDailyStatsRecords?.find(
      (keywordsDailyStatsRecord) => keywordsDailyStatsRecord.date.toString() === date
    );
    const averagePosition = record?.averagePosition?.toString() ?? '-';
    const averageShareOfVoice =
      record?.averageShareOfVoice != null ? record.averageShareOfVoice.toString() + '%' : '-';

    return {
      created: date,
      prompt: prompt,
      averagePosition,
      averageShareOfVoice,
    };
  });

  return {
    dailyMonitorResponse: allRecords,
    statsTableData,
  };
};

export const prepareAndGetColorsForPieChart = (pieChartData: PieChartData[], firmName: string) => {
  const updatedColors = [...SOV_PIE_CHART_PALETTE];
  const selectedIndex = pieChartData.findIndex(
    (data) => normalizeKeyword(data.name).toLowerCase() === normalizeKeyword(firmName).toLowerCase()
  );

  if (selectedIndex !== -1) {
    updatedColors[selectedIndex] = SELECTED_BRAND_COLOR;
  }

  return pieChartData.map((data, index) => ({
    name: data.name,
    color: updatedColors[index % updatedColors.length],
  }));
};

export const getMonitorTypeIcon = (monitorType: MonitorType) => {
  switch (monitorType) {
    case 'GEMINI_API':
    case 'GEMINI_API_BROWSER':
    case 'GEMINI_SEARCH_BROWSER':
      return 'ico--gemini-bck';

    case 'GPT':
    case 'GPT_API_BROWSER':
    case 'GPT_SEARCH_BROWSER':
      return 'ico--gpt-bck';

    case 'CLAUDE_API':
      return 'ico--claude-bck';

    case 'DEEP_SEEK_API':
      return 'ico--deepseek-bck';

    case 'PERPLEXITY_API':
    case 'PERPLEXITY_SEARCH_BROWSER':
      return 'ico--perplexity-bck';

    case 'COPILOT_SEARCH_BROWSER':
      return 'ico--copilot-bck';

    case 'GROK_SEARCH_BROWSER':
    case 'GROK_API':
      return 'ico--grok-bck';

    case 'AI_OVERVIEW_NEW':
      return 'ico--ai-overview-bck';

    case 'AI_MODE':
    case 'AI_OVERVIEW':
      return 'ico--ai-mode';

    default:
      return null;
  }
};

export const getDomainsOptions = (
  domains: string[],
  domain: string | null
): DropdownItemData<string>[] => {
  const baseOptions = (domains ?? []).map((d) => ({
    name: removeHttpHttps(d),
    type: d,
  }));

  const allOption = { name: t('allDomains'), type: '' };

  if (domain && !baseOptions.find((o) => o.name === domain) && domain !== allOption.name) {
    baseOptions.push({ name: domain, type: domain });
  }

  return [allOption, ...baseOptions];
};

export const findSelectedDomain = (
  domain: string | null,
  domainsOptions: DropdownItemData<string>[]
): DropdownItemData<string> => {
  if (!domain) {
    return domainsOptions[0];
  }

  return domainsOptions.find((o) => o.name === domain) || { name: domain, type: domain };
};

export const filterSourcesByDomain = (domain: string | null, sources: LinksData[]) => {
  if (!domain || domain === '') {
    return sources;
  }

  return sources.filter((s) => s.url.includes(domain));
};

export const getKeywordMap = (keywordResponse: KeywordResponse[]): Map<string, string> => {
  const keywordNameMap = new Map<string, string>();
  for (const kw of keywordResponse) {
    keywordNameMap.set(kw.normalizedKeyword, kw.keyword);
  }

  return keywordNameMap;
};
